import { NavLink, generatePath } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  InputText,
  Select,
  formatDate,
  Checkbox,
  IconRight,
  IconAttention,
  IconLoader,
  Popup,
  IconTrash,
  ButtonPrimary,
  ButtonSize,
  IconSelected,
  IconChangeLoClock,
  PermissionEnum,
} from '@ui/src';
import {
  CofStatus,
  SignatureType,
  AccessRight,
} from '../../../hoc/common/types';
import { NatureOfControlEnum } from '../../../hoc/new-application/types';
import { CompanyItemTypes } from '../types';
import getCofStatus from '../../../helpers/get-cof-status';

import ROUTES from '../../../routes';

import cn from './overview-content.module.css';

const CompanyItem = ({
  value,
  removed,
  activePermissions,
  formValues,
  updateStatusInProgress,
  employeeName,
  approveInProgress,
  defaultFormValues,
  cofIsUpdate,
  onApprove,
  setFormValues,
  updateCofAction,
  setPopupIsVisible,
  setRejectReason,
  onUpdateCofStatus,
  popupIsVisible,
  rejectReason,
}: CompanyItemTypes) => {
  let type;

  if (formValues.companyOfficialType === 'MAIN_REPRESENTATIVE') {
    type = 'Main Representative';
  } else if (formValues.companyOfficialType === 'COMPANY_OFFICIAL') {
    type = 'Company Official';
  }

  const { cofStatus, cofStatusClassName } = getCofStatus(
    value?.status as CofStatus,
  );

  let rejectOkButtonText;
  let approveButtonText = '';

  if (updateStatusInProgress) {
    rejectOkButtonText = 'Loading...';
  } else if (
    !updateStatusInProgress &&
    value?.status === CofStatus.PENDING_REVIEW
  ) {
    rejectOkButtonText = 'Reject';
  } else {
    rejectOkButtonText = 'Archive';
  }

  if (value?.companyOfficialType === 'MAIN_REPRESENTATIVE') {
    approveButtonText = 'Main Representative';
  } else if (value?.companyOfficialType === 'COMPANY_OFFICIAL') {
    approveButtonText = 'COF';
  }

  const link = generatePath(ROUTES.CUSTOMERS_CORPORATE_PAGE, {
    customerId: (value?.organization.id as string) || '',
  });

  return (
    <div
      className={`${cn.addCompanyOfficialInputsWrapper} ${cn.addCompanyOfficialItemWrapper}`}
    >
      <div className={cn.companyOfficialTop}>
        <NavLink to={link} target="_blank" className={cn.companyOfficialName}>
          {value?.organization.companyName}

          <IconRight />
        </NavLink>
        <div className={cn.companyOfficialAcceptedDate}>
          {`${
            value?.approvedAt || value?.status === CofStatus.APPROVED
              ? 'Approved'
              : 'Appointed'
          } in ${formatDate(
            formValues.approvedAt
              ? formValues.approvedAt
              : formValues.createdAt,
          )}`}
          {!removed && employeeName && (
            <div className={cn.approvedBy}>{`Approved by ${employeeName}`}</div>
          )}
        </div>
      </div>
      <div className={cn.companyOfficialItemStatus}>
        <div className={cn.companyOfficialItemStatus}>
          {`${type} status:`}
          <span className={cofStatusClassName}>{cofStatus}</span>
        </div>
        {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
          <NavLink
            target="_blank"
            to={`/administration/change-logs?entityType=ORGANIZATION&entityId=${value?.organization.id}`}
            className={cn.cofChangeLogLink}
          >
            <IconChangeLoClock />
            Change Log
          </NavLink>
        )}
      </div>
      {value?.status === CofStatus.PENDING_REVIEW && (
        <div className={cn.approveCofWrapper}>
          <ButtonPrimary
            size={ButtonSize.SMALL}
            text={
              approveInProgress ||
              (!isEqual(formValues, defaultFormValues) && cofIsUpdate)
                ? 'Loading...'
                : `Approve ${approveButtonText}`
            }
            icon={
              approveInProgress ||
              (!isEqual(formValues, defaultFormValues) &&
                cofIsUpdate) ? undefined : (
                <IconSelected />
              )
            }
            disabled={
              approveInProgress ||
              (!isEqual(formValues, defaultFormValues) && cofIsUpdate)
            }
            onClick={onApprove}
          />
          <div className={cn.approveCofText}>
            The employee is awaiting confirmation of Company Official status
          </div>
        </div>
      )}
      {removed && (
        <div className={cn.removedCompanyOfficialBlock}>
          <div className={cn.removedLeft}>
            <IconAttention />
            {`${
              value?.status === CofStatus.REJECTED ? 'Rejected' : 'Archived'
            } from the company in ${formatDate(
              value?.status === CofStatus.REJECTED
                ? value.rejectedAt
                : (value?.archivedAt as string),
            )} ${employeeName ? `by ${employeeName}` : ''}`}
          </div>
          {value?.rejectionReason && (
            <div className={cn.rejectionReason}>{`${
              value?.status === CofStatus.REJECTED ? 'Rejection' : 'Archive'
            } reason: ${value.rejectionReason}`}</div>
          )}
        </div>
      )}

      <div className={cn.companyOfficialValueWrapper}>
        <InputText
          name="position"
          value={formValues.position}
          title="Position"
          placeholder="Enter position"
          onChange={(e) => {
            setFormValues({ ...formValues, position: e.target.value });
          }}
          disabled={removed}
        />
      </div>
      <div className={cn.companyOfficialValueWrapper}>
        <Checkbox
          type="checkbox"
          name={`havingControl-${value?.id}`}
          checked={Boolean(formValues.havingControl)}
          title="Having a control in company"
          onChange={(e) => {
            setFormValues({ ...formValues, havingControl: e.target.checked });
          }}
          disabled={removed}
        />
      </div>
      {formValues.havingControl && (
        <div
          className={`${cn.companyOfficialValueWrapper} ${cn.companyOfficialValueWrapperFlex}`}
        >
          <div className={cn.inputFlex}>
            <InputText
              name="percentageOfControl"
              value={formValues.percentage}
              title="Percentage of control"
              placeholder="Enter Percentage of control"
              onChange={(e) => {
                setFormValues({
                  ...formValues,
                  percentage: e.target.value,
                });
              }}
              disabled={removed}
            />
          </div>
          <div className={cn.inputFlex}>
            <Select
              list={[
                {
                  name: 'Direct Control',
                  value: NatureOfControlEnum.DIRECT_CONTROL,
                },
                {
                  name: 'Indirect Control',
                  value: NatureOfControlEnum.INDIRECT_CONTROL,
                },
              ]}
              name="natureOfControl"
              value={formValues.natureOfControl}
              title="Nature of control"
              placeholder="Select Nature of control"
              onChange={(e) => {
                setFormValues({
                  ...formValues,
                  natureOfControl: e.target.value as NatureOfControlEnum,
                });
              }}
              disabled={removed}
            />
          </div>
        </div>
      )}
      <div className={cn.companyOfficialValueWrapper}>
        <Select
          list={[
            {
              name: 'None',
              value: SignatureType.NONE,
            },
            {
              name: 'Single',
              value: SignatureType.SINGLE,
            },
          ]}
          name="signatureType"
          value={formValues.signatureType}
          title="Signature Type"
          onChange={(e) => {
            setFormValues({
              ...formValues,
              signatureType: e.target.value as SignatureType,
            });
          }}
          fullWidth
          disabled={removed}
        />
      </div>
      <div className={cn.companyOfficialValueWrapper}>
        <Select
          list={[
            {
              name: 'None',
              value: AccessRight.NONE,
            },
            {
              name: 'Viewer',
              value: AccessRight.VIEWER,
            },
          ]}
          name="accessRight"
          value={formValues.accessRight || AccessRight.VIEWER}
          onChange={(e) => {
            setFormValues({
              ...formValues,
              accessRight: e.target.value as SignatureType,
            });
          }}
          title="Access Right"
          disabled={removed}
          fullWidth
        />
      </div>
      {!removed && (
        <div className={cn.cofControls}>
          <button
            type="button"
            className={`${cn.companyOfficialSubmit} ${
              cofIsUpdate ? cn.cofUpdate : ''
            }`}
            onClick={updateCofAction}
            disabled={isEqual(formValues, defaultFormValues)}
          >
            {cofIsUpdate ? <IconLoader width={20} height={20} /> : 'Save'}
          </button>
          <button
            type="button"
            className={cn.companyOfficialDeleteButton}
            onClick={() => setPopupIsVisible(true)}
          >
            <IconTrash />
            {value?.status === CofStatus.PENDING_REVIEW ? 'Reject' : 'Archive'}
          </button>
        </div>
      )}
      <Popup
        parent={document.getElementById('popup-modal') as HTMLElement}
        title={`Remove from ${value?.organization.companyName}?`}
        description={`${formValues?.user?.profile?.firstName} ${
          formValues?.user?.profile?.lastName
        } will be ${
          value?.status === CofStatus.PENDING_REVIEW ? 'rejected' : 'archived'
        } from the ${
          value?.organization.companyName
        }. But he will remain on the list of deleted official.`}
        onCancelClick={() => {
          setPopupIsVisible(false);
          setRejectReason('');
        }}
        onOkClick={() =>
          onUpdateCofStatus(
            value?.status === CofStatus.PENDING_REVIEW
              ? CofStatus.REJECTED
              : CofStatus.ARCHIVED,
          )
        }
        cancelButtonText="Cancel"
        okButtonText={rejectOkButtonText}
        isVisible={popupIsVisible}
        isLoading={updateStatusInProgress}
      >
        <div>
          {value?.status === CofStatus.PENDING_REVIEW && (
            <div className={cn.rejetReasonInput}>
              <InputText
                name="rejectReason"
                title="Reject reason"
                placeholder="Reject reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </div>
          )}
        </div>
      </Popup>
    </div>
  );
};

export default CompanyItem;
